import React from 'react';
import { 
  Calendar, 
  Users, 
  MessageSquare, 
  Phone, 
  BarChart3, 
  FileText, 
  Clock,
  CheckCircle,
  TrendingUp,
  DollarSign,
  Star,
  MapPin,
  Mail
} from 'lucide-react';

// KPI Dashboard Snapshot
export const KPIDashboardSnapshot: React.FC = () => {
  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Business Dashboard</h3>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span className="text-xs text-gray-400">Live</span>
        </div>
      </div>
      
      {/* KPI Cards Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-xl border border-purple-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-white" />
            </div>
            <span className="text-xs text-green-400 font-medium">+28%</span>
          </div>
          <div className="text-2xl font-bold text-white mb-1">$24,580</div>
          <div className="text-xs text-gray-400">Monthly Revenue</div>
        </div>
        
        <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-xl border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <Calendar className="h-4 w-4 text-white" />
            </div>
            <span className="text-xs text-green-400 font-medium">+12%</span>
          </div>
          <div className="text-2xl font-bold text-white mb-1">147</div>
          <div className="text-xs text-gray-400">Bookings This Month</div>
        </div>
        
        <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-xl border border-green-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
              <Users className="h-4 w-4 text-white" />
            </div>
            <span className="text-xs text-green-400 font-medium">98%</span>
          </div>
          <div className="text-2xl font-bold text-white mb-1">8</div>
          <div className="text-xs text-gray-400">Team Performance</div>
        </div>
        
        <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur-xl border border-orange-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Star className="h-4 w-4 text-white" />
            </div>
            <span className="text-xs text-green-400 font-medium">4.9</span>
          </div>
          <div className="text-2xl font-bold text-white mb-1">4.9</div>
          <div className="text-xs text-gray-400">Customer Rating</div>
        </div>
      </div>
      
      {/* Mini Chart */}
      <div className="bg-gray-800/50 backdrop-blur-xl border border-gray-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-white">Revenue Trend</span>
          <TrendingUp className="h-4 w-4 text-green-400" />
        </div>
        <div className="flex items-end space-x-1 h-16">
          {[40, 65, 45, 80, 60, 95, 75, 85, 70, 90, 85, 100].map((height, i) => (
            <div
              key={i}
              className="bg-gradient-to-t from-purple-500 to-pink-500 rounded-sm flex-1 opacity-80 hover:opacity-100 transition-opacity"
              style={{ height: `${height}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Team Management Snapshot
export const TeamSnapshot: React.FC = () => {
  const teamMembers = [
    { name: 'Sarah Johnson', role: 'Senior Technician', avatar: 'SJ', status: 'online', bookings: 12 },
    { name: 'Mike Chen', role: 'Lead Detailer', avatar: 'MC', status: 'busy', bookings: 8 },
    { name: 'Emma Davis', role: 'Service Manager', avatar: 'ED', status: 'online', bookings: 15 },
    { name: 'Alex Rodriguez', role: 'Technician', avatar: 'AR', status: 'offline', bookings: 6 }
  ];

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Team Management</h3>
        <div className="text-xs text-gray-400">4 members online</div>
      </div>
      
      <div className="space-y-4">
        {teamMembers.map((member, index) => (
          <div key={index} className="bg-gray-800/50 backdrop-blur-xl border border-gray-700 rounded-xl p-4 hover:bg-gray-700/50 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {member.avatar}
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-gray-800 ${
                    member.status === 'online' ? 'bg-green-400' :
                    member.status === 'busy' ? 'bg-yellow-400' : 'bg-gray-400'
                  }`} />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">{member.name}</div>
                  <div className="text-xs text-gray-400">{member.role}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-white">{member.bookings}</div>
                <div className="text-xs text-gray-400">bookings</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Team Efficiency</span>
          <span className="text-green-400 font-semibold">94%</span>
        </div>
      </div>
    </div>
  );
};

// Messages/SMS Snapshot
export const MessagesSnapshot: React.FC = () => {
  const conversations = [
    { name: 'Jessica Williams', message: 'Thanks for the great service! When can I book again?', time: '2m ago', unread: 2, type: 'sms' },
    { name: 'David Miller', message: 'Hi, I need to reschedule my appointment for tomorrow', time: '5m ago', unread: 1, type: 'sms' },
    { name: 'Lisa Thompson', message: 'Invoice received. Payment processed successfully.', time: '12m ago', unread: 0, type: 'email' },
    { name: 'Robert Garcia', message: 'Could you send me the quote for the full detail package?', time: '1h ago', unread: 0, type: 'sms' }
  ];

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Customer Messages</h3>
        <div className="flex items-center space-x-2">
          <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-semibold">3</div>
        </div>
      </div>
      
      <div className="space-y-3">
        {conversations.map((conv, index) => (
          <div key={index} className="bg-gray-800/50 backdrop-blur-xl border border-gray-700 rounded-xl p-4 hover:bg-gray-700/50 transition-all duration-300 cursor-pointer">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {conv.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  {conv.unread > 0 && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-semibold">
                      {conv.unread}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-medium text-white">{conv.name}</span>
                    {conv.type === 'sms' ? (
                      <MessageSquare className="h-3 w-3 text-green-400" />
                    ) : (
                      <Mail className="h-3 w-3 text-blue-400" />
                    )}
                  </div>
                  <p className="text-xs text-gray-400 truncate">{conv.message}</p>
                </div>
              </div>
              <span className="text-xs text-gray-500 ml-2">{conv.time}</span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Active conversation preview */}
      <div className="mt-6 bg-gray-800/30 backdrop-blur-xl border border-gray-600 rounded-xl p-4">
        <div className="flex items-center space-x-2 mb-3">
          <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
            JW
          </div>
          <span className="text-sm font-medium text-white">Jessica Williams</span>
          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
        </div>
        <div className="space-y-2">
          <div className="bg-blue-500 text-white text-sm rounded-2xl rounded-bl-md px-3 py-2 max-w-xs ml-auto">
            Thanks for choosing us! We'd love to have you back.
          </div>
          <div className="bg-gray-700 text-white text-sm rounded-2xl rounded-br-md px-3 py-2 max-w-xs">
            Thanks for the great service! When can I book again?
          </div>
        </div>
      </div>
    </div>
  );
};

// Proposals Snapshot
export const ProposalsSnapshot: React.FC = () => {
  const proposals = [
    { id: 'PROP-001', customer: 'Michael Johnson', service: 'Premium Detail Package', amount: 450, status: 'pending', date: 'Mar 15' },
    { id: 'PROP-002', customer: 'Sarah Davis', service: 'Paint Correction + Ceramic', amount: 1200, status: 'approved', date: 'Mar 14' },
    { id: 'PROP-003', customer: 'Tom Wilson', service: 'Interior Deep Clean', amount: 180, status: 'pending', date: 'Mar 13' },
    { id: 'PROP-004', customer: 'Anna Martinez', service: 'Full Detail + Protection', amount: 850, status: 'sent', date: 'Mar 12' }
  ];

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Recent Proposals</h3>
        <div className="text-xs text-gray-400">$2,680 pending</div>
      </div>
      
      <div className="space-y-3">
        {proposals.map((proposal, index) => (
          <div key={index} className="bg-gray-800/50 backdrop-blur-xl border border-gray-700 rounded-xl p-4 hover:bg-gray-700/50 transition-all duration-300">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">{proposal.id}</div>
                  <div className="text-xs text-gray-400">{proposal.customer}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-white">${proposal.amount}</div>
                <div className="text-xs text-gray-400">{proposal.date}</div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-300 truncate flex-1 mr-3">
                {proposal.service}
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                proposal.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                proposal.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-blue-500/20 text-blue-400'
              }`}>
                {proposal.status}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-white">67%</div>
            <div className="text-xs text-gray-400">Approval Rate</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-white">$2,680</div>
            <div className="text-xs text-gray-400">Pending Value</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-white">4</div>
            <div className="text-xs text-gray-400">This Week</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calling Snapshot
export const CallingSnapshot: React.FC = () => {
  const callHistory = [
    { name: 'Jennifer Adams', number: '+1 (555) 123-4567', type: 'incoming', duration: '3:45', time: '10:30 AM', status: 'completed' },
    { name: 'Mark Thompson', number: '+1 (555) 987-6543', type: 'outgoing', duration: '1:22', time: '9:15 AM', status: 'completed' },
    { name: 'Unknown', number: '+1 (555) 456-7890', type: 'missed', duration: '0:00', time: '8:45 AM', status: 'missed' },
    { name: 'Lisa Chen', number: '+1 (555) 234-5678', type: 'incoming', duration: '5:12', time: '8:20 AM', status: 'completed' }
  ];

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Call Management</h3>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span className="text-xs text-gray-400">Ready</span>
        </div>
      </div>
      
      {/* Active Call Simulation */}
      <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-xl border border-green-500/30 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center animate-pulse">
              <Phone className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="text-sm font-medium text-white">Incoming Call</div>
              <div className="text-xs text-gray-300">+1 (555) 123-4567</div>
            </div>
          </div>
          <div className="flex space-x-2">
            <button className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors">
              <Phone className="h-5 w-5 text-white transform rotate-135" />
            </button>
            <button className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors">
              <Phone className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Call History */}
      <div className="space-y-3">
        {callHistory.map((call, index) => (
          <div key={index} className="bg-gray-800/50 backdrop-blur-xl border border-gray-700 rounded-xl p-3 hover:bg-gray-700/50 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  call.type === 'incoming' ? 'bg-green-500/20 text-green-400' :
                  call.type === 'outgoing' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  <Phone className={`h-4 w-4 ${
                    call.type === 'outgoing' ? 'transform rotate-12' : 
                    call.type === 'missed' ? 'transform rotate-135' : ''
                  }`} />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">{call.name}</div>
                  <div className="text-xs text-gray-400">{call.number}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-white">{call.time}</div>
                <div className="text-xs text-gray-400">{call.duration}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-white">24</div>
            <div className="text-xs text-gray-400">Calls Today</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-white">2:34</div>
            <div className="text-xs text-gray-400">Avg Duration</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-white">92%</div>
            <div className="text-xs text-gray-400">Answer Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calendar Booking Snapshot
export const CalendarSnapshot: React.FC = () => {
  const todaysBookings = [
    { time: '9:00 AM', customer: 'John Smith', service: 'Full Detail', duration: '3h', status: 'confirmed' },
    { time: '10:30 AM', customer: 'Mary Johnson', service: 'Wash & Wax', duration: '1.5h', status: 'in-progress' },
    { time: '1:00 PM', customer: 'David Wilson', service: 'Interior Clean', duration: '2h', status: 'confirmed' },
    { time: '3:30 PM', customer: 'Sarah Davis', service: 'Paint Correction', duration: '4h', status: 'confirmed' }
  ];

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Today's Schedule</h3>
        <div className="text-xs text-gray-400">March 15, 2025</div>
      </div>
      
      {/* Mini Calendar Header */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <div key={i} className="text-center text-xs text-gray-400 py-2">{day}</div>
        ))}
        {Array.from({ length: 35 }, (_, i) => {
          const day = i - 6; // Start from previous month
          const isToday = day === 15;
          const hasBooking = [12, 13, 15, 16, 18].includes(day);
          return (
            <div
              key={i}
              className={`text-center text-xs py-1 rounded ${
                isToday 
                  ? 'bg-purple-500 text-white font-semibold' 
                  : day > 0 && day <= 31 
                    ? hasBooking 
                      ? 'text-blue-400 font-medium' 
                      : 'text-gray-300 hover:text-white' 
                    : 'text-gray-600'
              }`}
            >
              {day > 0 && day <= 31 ? day : ''}
            </div>
          );
        })}
      </div>
      
      {/* Today's Bookings */}
      <div className="space-y-3">
        {todaysBookings.map((booking, index) => (
          <div key={index} className="bg-gray-800/50 backdrop-blur-xl border border-gray-700 rounded-xl p-4 hover:bg-gray-700/50 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="text-sm font-medium text-purple-400 min-w-16">
                  {booking.time}
                </div>
                <div className="w-px h-8 bg-gray-600"></div>
                <div>
                  <div className="text-sm font-medium text-white">{booking.customer}</div>
                  <div className="text-xs text-gray-400">{booking.service} • {booking.duration}</div>
                </div>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                booking.status === 'in-progress' ? 'bg-blue-500/20 text-blue-400' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>
                {booking.status === 'in-progress' ? 'In Progress' : 'Confirmed'}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-white">4</div>
            <div className="text-xs text-gray-400">Today</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-white">12</div>
            <div className="text-xs text-gray-400">This Week</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-white">89%</div>
            <div className="text-xs text-gray-400">Utilization</div>
          </div>
        </div>
      </div>
    </div>
  );
};
